import Text from "common/Typography/Text";
import {
  LanguageNameSpaces,
  LoginRequest,
  OAuthUserRequest,
} from "@shared/types";
import { AuthDescription, PrimaryTitle, Wrapper } from "features/auth/styles";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import React from "react";
import { Security } from "common/Icons/Icons";
import VerificationCode from "features/auth/VerificationCode";
import { sharedLib } from "@shared/services/shared.service";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";
import { triggerSaveBiometric } from "services/webview/webViewBridgeUtils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TwoFALoginProps {
  loginDetails: LoginRequest | OAuthUserRequest;
  isBiometricLogin?: boolean;
  onFinalizedLogin: () => void;
}

export const TwoFALogin = ({
  loginDetails,
  isBiometricLogin,
  onFinalizedLogin,
}: TwoFALoginProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [login, loginStatus] =
    sharedLib.sharedApi.authentication.useLoginMutation();
  const [oauthUser, oauthUserStatus] =
    sharedLib.sharedApi.authentication.useOauthUserMutation();
  const isLoading = loginStatus.isLoading || oauthUserStatus.isLoading;
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { isNewLook } = useTheme();
  const { display: displayResultMessage, error } = useAuthResultMessage();

  const onSubmit = (code: string) => {
    const loginRequest = { ...loginDetails, two_factor_token: code };
    if ("email" in loginDetails) {
      displayResultMessage(login(loginRequest as LoginRequest).unwrap())
        .then(() => {
          onFinalizedLogin();
          if (!isBiometricLogin) {
            return triggerSaveBiometric(loginDetails);
          }
        })
        .catch(() => {});
    } else {
      displayResultMessage(oauthUser(loginRequest as OAuthUserRequest).unwrap())
        .then(async (result) => {
          onFinalizedLogin();
          if (result?.user_id) {
            handleUserTempOAuthData(result.user_id);
          }
        })
        .catch(() => {});
    }
  };

  return (
    <Wrapper $isMobile={isMobile}>
      {isNewLook ? (
        <>
          <PrimaryTitle $isMobile={isMobile}>
            {t("two_fa.title_new_look")}
          </PrimaryTitle>
          <AuthDescription>{t("two_fa.description")}</AuthDescription>
        </>
      ) : (
        <>
          <PrimaryTitle $isMobile={isMobile}>{t("two_fa.title")}</PrimaryTitle>
          <Security style={{ marginBottom: 24 }} />
          <Text>{t("two_fa.description")}</Text>
        </>
      )}
      <VerificationCode
        numOfDigits={NUM_OF_DIGITS_CODE}
        title={t(
          isNewLook
            ? "two_fa.google_verification_code_new_look"
            : "two_fa.google_verification_code"
        )}
        onSubmit={onSubmit}
        isLoading={isLoading}
        additionalItems={
          error ? (
            <AuthInlineError message={error} style={{ margin: "16px 0 0" }} />
          ) : undefined
        }
      />
    </Wrapper>
  );
};

const NUM_OF_DIGITS_CODE = 6;
